/**
 * @file
 * A JavaScript file for the site.
 *
 * @copyright Copyright 2022 Palantir.net
 */

// JavaScript should be made compatible with libraries other than jQuery by
// wrapping it with an "anonymous closure". See:

// - http://www.adequatelygood.com/2010/3/JavaScript-Module-Pattern-In-Depth

(function ($) {
  /// /////////////////////////////////////
  // Header/menu interactions
  /// /////////////////////////////////////

  // Shared focusable selector (matches the pattern already used in coe-nav.js)
  const menuFocusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  // The visible main (mega) menu panel. The variant prefix differs by header
  // type (`system-menu__dropdown` vs `learning-menu__dropdown`), but every
  // variant carries `.menus__dropdown`, so that is what we target.
  function getMainMenuPanel() {
    return document.querySelector('.menus__dropdown');
  }

  // The visible button that opens/closes the main (mega) menu. On the live site
  // it carries both classes, but we match either so focus management still runs
  // if only one is present.
  function getMainMenuTrigger() {
    return document.querySelector('.button--main-menu, .desktop-menu-button');
  }

  // Whether the main (mega) menu panel is currently open
  function isMainMenuOpen() {
    return $('.menus__hamburger-input').is(':checked');
  }

  // ADA: wire the trigger button and panel together so assistive tech can
  // announce the menu as an expandable dialog and knows which region it opens.
  function initMainMenuAria() {
    const panel = getMainMenuPanel();
    const trigger = getMainMenuTrigger();
    if (!panel) {
      return;
    }
    if (!panel.id) {
      panel.id = 'mainMenuPanel';
    }
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    if (
      !panel.getAttribute('aria-label') &&
      !panel.getAttribute('aria-labelledby')
    ) {
      panel.setAttribute('aria-label', 'Main menu');
    }
    // ADA: the section headings (e.g. "I would like to", "Baptist Health
    // Resources") are non-focusable, so keyboard/screen reader users tabbing
    // through the menu never hear them. Name each group's <nav> region with its
    // heading via aria-labelledby so the section is announced on entry, and so
    // the otherwise-identical <nav> landmarks are distinguishable.
    const headings = panel.querySelectorAll('.dropdown-menu__heading');
    Array.prototype.forEach.call(headings, function (heading, index) {
      const region = heading.parentElement && heading.parentElement.querySelector('nav');
      if (!region) {
        return;
      }
      if (!heading.id) {
        heading.id = 'mainMenuSection' + (index + 1);
      }
      if (
        !region.getAttribute('aria-label') &&
        !region.getAttribute('aria-labelledby')
      ) {
        region.setAttribute('aria-labelledby', heading.id);
      }
    });
    // ADA: advertise how to leave the modal menu so users are not stuck in the
    // focus loop (WCAG 2.1.2 No Keyboard Trap). A visually-hidden description,
    // referenced via aria-describedby, is announced when focus enters the
    // dialog — this makes the Escape / Close Main Menu exits discoverable.
    if (!panel.getAttribute('aria-describedby')) {
      let hint = panel.querySelector('.js-main-menu-exit-hint');
      if (!hint) {
        hint = document.createElement('span');
        hint.className = 'js-main-menu-exit-hint element-invisible';
        hint.textContent =
          'Press the Escape key or activate the Close Main Menu button to exit this menu.';
        panel.insertBefore(hint, panel.firstChild);
      }
      if (!hint.id) {
        hint.id = 'mainMenuExitHint';
      }
      panel.setAttribute('aria-describedby', hint.id);
    }
    // ADA: name any remaining unnamed <nav> landmark in the menu (e.g. the
    // utility links) so screen reader users can distinguish the regions.
    const utilityNav = panel.querySelector('nav.dropdown-utility__items');
    if (
      utilityNav &&
      !utilityNav.getAttribute('aria-label') &&
      !utilityNav.getAttribute('aria-labelledby')
    ) {
      utilityNav.setAttribute('aria-label', 'Menu utilities');
    }
    if (trigger) {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', isMainMenuOpen() ? 'true' : 'false');
      trigger.setAttribute('aria-haspopup', 'dialog');
    }
  }

  // Open the main (mega) menu and move focus into it (ADA). Moving focus into
  // the labelled dialog means keyboard users land in the menu immediately, and
  // screen readers announce that the menu opened — instead of having to tab
  // past unrelated header controls before discovering the panel.
  function openMainMenu() {
    const panel = getMainMenuPanel();
    const trigger = getMainMenuTrigger();
    // Close the search panel if it happens to be open.
    $('.menus__search').removeClass('js-search-active');
    $('.menus__overlay').removeClass('js-overlay-active');
    $('.menus__hamburger-input').prop('checked', true);
    $('.desktop-menu-button').addClass('menu-is-open');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }
    if (panel) {
      panel.removeAttribute('hidden');
      // The panel is `display:none` until the `:checked` sibling rule applies,
      // so defer focus until the browser has flipped it to visible; focusing a
      // hidden element is a no-op.
      window.setTimeout(function () {
        panel.focus();
      }, 0);
    }
  }

  // Close the main (mega) menu and return focus to the open/close button (ADA)
  function closeMainMenu() {
    const trigger = getMainMenuTrigger();
    $('.coe-menu').attr('hidden', 'hidden').removeClass('menu-is-open');
    $('.menus__hamburger-input').prop('checked', false);
    $('.menus__overlay').removeClass('js-overlay-active');
    $('.desktop-menu-button').removeClass('menu-is-open');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    } else {
      $('.menus__hamburger-input').focus();
    }
  }

  // Set up ARIA relationships once the DOM is ready.
  $(initMainMenuAria);

  // Whether the search panel is currently open
  function isSearchOpen() {
    return $('.menus__search').hasClass('js-search-active');
  }

  // Close the search panel and return focus to the search toggle button (ADA)
  function closeSearch() {
    $('.menus__search').removeClass('js-search-active');
    $('.menus__overlay').removeClass('js-overlay-active');
    const $toggle = $('#buttonSearch').length
      ? $('#buttonSearch')
      : $('.header-bar__ctas .button--search');
    if ($toggle.length) {
      $toggle.first().focus();
    }
  }

  // Trigger menu dropdown when user clicks ENTER on hamburger menu
  $('.menus__hamburger-input').keypress(function (e) {
    if (e.which == 13) {
      // Toggle through the shared open/close helpers so focus is moved into the
      // panel on open and returned to the trigger on close (ADA).
      if ($(this).is(':checked')) {
        closeMainMenu();
      } else {
        openMainMenu();
      }
    }
  });

  // Show and hide search dropdown
  $('#buttonSearch').click(() => {
    if ($('.menus__search').hasClass('js-search-active')) {
      $('.menus__search').removeClass('js-search-active');
      $('.menus__overlay').removeClass('js-overlay-active');
    } else {
      $('.menus__search').addClass('js-search-active');
      $('.menus__overlay').addClass('js-overlay-active');
      $('.menus__hamburger-input').prop('checked', false);
      $('.desktop-menu-button').removeClass('menu-is-open');
      // ADA: opening search closes the main menu, so keep the menu trigger's
      // expanded state honest instead of leaving a stale aria-expanded="true".
      const mainMenuTrigger = getMainMenuTrigger();
      if (mainMenuTrigger) {
        mainMenuTrigger.setAttribute('aria-expanded', 'false');
      }
      // ADA: move focus into the opened search panel (first focusable = search input),
      // queried relative to the panel so it does not depend on a specific input id.
      const searchPanel = document.querySelector('.menus__search');
      const firstFocusable = searchPanel
        ? searchPanel.querySelector(menuFocusableSelector)
        : null;
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  });

  // Close search dropdown and navigate back to search button
  $('.close-search').click(() => {
    closeSearch();
  });

  // Close main menu dropdown and navigate back to hamburger button
  $('.close-main-menu').click(() => {
    closeMainMenu();
  });

  // new menu button in main nav items (matches both the `button--main-menu` and
  // `desktop-menu-button` classes present on the live trigger)
  $('.button--main-menu, .desktop-menu-button').click(function () {
    if (isMainMenuOpen()) {
      closeMainMenu();
    } else {
      openMainMenu();
    }
  });

  // Sync focus management when the checkbox itself is toggled directly (e.g. via
  // a label). Its `checked` state has already flipped natively at this point.
  $('.menus__hamburger-input').click(function () {
    if ($(this).is(':checked')) {
      openMainMenu();
    } else {
      closeMainMenu();
    }
  });

  // Hide dropdown when user clicks on overlay
  $('.menus__overlay').click(() => {
    if (isSearchOpen()) {
      closeSearch();
    } else if ($('.menus__hamburger-input').is(':checked')) {
      closeMainMenu();
    }
  });

  // ADA: Esc closes the main menu and keyboard focus stays trapped inside the
  // open panel until it is closed.
  $(document).on('keydown', function (e) {
    // Only act when the main menu panel is open (not the search dropdown)
    if (!isMainMenuOpen() || $('.menus__search').hasClass('js-search-active')) {
      return;
    }

    const key = e.key;

    // Esc closes the menu and restores focus to the toggle button
    if (key === 'Escape' || key === 'Esc') {
      closeMainMenu();
      return;
    }

    // Tab / Shift+Tab: keep focus looping inside the open panel
    if (key === 'Tab') {
      const panel = getMainMenuPanel();
      if (!panel) {
        return;
      }
      const focusables = $(panel)
        .find(menuFocusableSelector)
        .filter(':visible');
      if (!focusables.length) {
        return;
      }
      const first = focusables.first()[0];
      const last = focusables.last()[0];
      const active = document.activeElement;

      if (!panel.contains(active) && active !== panel) {
        // Focus is outside the open menu (e.g. still on the trigger, or it
        // tabbed out into unrelated header controls) — pull it back in so the
        // menu behaves as a modal dialog.
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // ADA: Esc closes the search panel and keyboard focus stays trapped inside the
  // open panel until it is closed.
  $(document).on('keydown', function (e) {
    if (!isSearchOpen()) {
      return;
    }

    const key = e.key;

    // Esc closes search and restores focus to the toggle button
    if (key === 'Escape' || key === 'Esc') {
      closeSearch();
      return;
    }

    // Tab / Shift+Tab: keep focus inside the open search panel
    if (key === 'Tab') {
      const $panel = $('.menus__search');
      const focusables = $panel.find(menuFocusableSelector).filter(':visible');
      if (!focusables.length) {
        return;
      }
      const first = focusables.first()[0];
      const last = focusables.last()[0];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (!$panel[0].contains(active)) {
        // Focus somehow left the panel while open — pull it back to the first item
        e.preventDefault();
        first.focus();
      }
    }
  });

  /// /////////////////////////////////////
  // Video Modal
  /// /////////////////////////////////////

  $('.js-modal-btn').each(function () {
    $(this).modalVideo({ channel: $(this).data('video-channel') });
  });

  /// /////////////////////////////////////
  // Secondary nav
  /// /////////////////////////////////////

  function sectionNavDisplay() {
    const $window = $(window);
    const $sectionNav = $('.section-nav__dropdown');
    const $bp3 = 900;

    if ($window.width() > $bp3) {
      $sectionNav.show();
    } else {
      $sectionNav.hide();
    }
  }

  // Execute on load and resize
  $(window).on('load', sectionNavDisplay);
  $(window).on('resize', sectionNavDisplay);

  // Trigger the mobile section nav dropdown
  $('.section-nav__trigger').click(function () {
    if ($(this).hasClass('js-is-open')) {
      // Close dropdown if click again
      $(this).removeClass('js-is-open').attr('aria-expanded', 'false');
      $(this).siblings('.section-nav__dropdown').slideUp();
    } else {
      // Open dropdown on click
      $(this).addClass('js-is-open').attr('aria-expanded', 'true');
      $(this).siblings('.section-nav__dropdown').slideDown({
        inherit_select_classes: true,
      });
    }
  });

  // Trigger section nav nested children drawer
  $('.section-nav__children-button').click(function () {
    if ($(this).hasClass('js-is-open')) {
      // Close dropdown if click again
      $(this).removeClass('js-is-open').attr('aria-expanded', 'false');
      $(this).siblings('.section-nav__sublinks').slideUp();
    } else {
      // Open dropdown on click
      $(this).addClass('js-is-open').attr('aria-expanded', 'true');
      $(this).siblings('.section-nav__sublinks').slideDown();
    }
  });

  // Recusively check DOM to be sure nav items are open if current item.
  function showHighestLevelSublinks(activeLink) {
    // Open the highest-level parent (always a `.section-nav__item`)
    const topLevelItem = activeLink.closest('.section-nav__item');
    if (topLevelItem.length) {
        const topLevelSublinks = topLevelItem.find('> .section-nav__sublinks');
        if (topLevelSublinks.length) {
            topLevelSublinks.slideDown();
        }

        const topLevelButton = topLevelItem.find('> .section-nav__children-button');
        if (topLevelButton.length) {
            topLevelButton.addClass('js-is-open').attr('aria-expanded', 'true');
        }
    }

    // Open the immediate level of the active item
    const parentEl = activeLink.closest('.section-nav__subitem');
    const activeSublinks = parentEl.find('> .section-nav__sublinks');
    const activeButton = parentEl.find('> .section-nav__children-button');
    if (activeSublinks && activeButton) {
          activeButton.addClass('js-is-open').attr('aria-expanded', 'true');
          activeSublinks.slideDown();
    }

    // Traverse up the DOM tree to open all parent levels
    let currentItem = activeLink.closest('.section-nav__subitem');
    while (currentItem && currentItem.length) {
        // Open the current level's sublinks
        const sublinks = currentItem.closest('.section-nav__sublinks');
        if (sublinks.length) {
            sublinks.slideDown();
        }

        // Open the corresponding button for this level
        const parentButton = sublinks.siblings('.section-nav__children-button');
        if (parentButton.length) {
            parentButton.addClass('js-is-open').attr('aria-expanded', 'true');
        }

        // Move up to the parent item
        currentItem = currentItem.closest('.section-nav__sublinks').closest('.section-nav__subitem');
    }
  }
  
  // Run recursive funtion a sublink is active
  if($('.section-nav__sublink.active').length) {
    $('.section-nav__sublink.active').each(function () {
      showHighestLevelSublinks($(this));
    });
  }

  // Just open sublinks if active is top level item
  if($('.section-nav__link.active').length) {
    const parentEl = $('.section-nav__link.active').closest('.section-nav__item');;
    const activeSublinks = parentEl.find('> .section-nav__sublinks');
    const activeButton = parentEl.find('> .section-nav__children-button');
    if (activeSublinks && activeButton) {
          activeButton.addClass('js-is-open').attr('aria-expanded', 'true');
          activeSublinks.slideDown();
    }
  }

  /// /////////////////////////////////////
  // Chosen js
  /// /////////////////////////////////////

  // Init chosen js
  $('.js-chosen-select').chosen({
    inherit_select_classes: true,
    width: '100%',
  });

  // Hide insurances results section
  $('.page .insurances-results').hide();

  // Show insurances results on dropdown selection
  $('.js-chosen-insurance-select')
    .chosen({
      inherit_select_classes: true,
      width: '100%',
    })
    .change(() => {
      $('.page .insurances-results').slideDown();
    });

  $('.js-chosen-select, .js-chosen-insurance-select').each(function () {
    const $select = $(this);
    const id = $select.attr('id');
    const labelText =
      $('label[for="' + id + '"]').text().trim() ||
      $select.attr('data-placeholder') ||
      'Filter options';
    $select
      .next('.chosen-container')
      .find('.chosen-search input, .chosen-search-input')
      .attr('aria-label', labelText);
  });

  /// /////////////////////////////////////
  // Rail filter
  /// /////////////////////////////////////

  // Trigger the mobile filter dropdown
  $('.rail-filters--services .rail-filters__trigger').click(function () {
    if ($(this).hasClass('js-is-open')) {
      // Close dropdown if click again
      $(this).removeClass('js-is-open');
      $(this).siblings('.rail-filters__dropdown').slideUp();
    } else {
      // Open dropdown on click
      $(this).addClass('js-is-open');
      $(this).siblings('.rail-filters__dropdown').slideDown({
        inherit_select_classes: true,
      });
    }
  });

  // conditionally display physician practice types
  $('#facility-type-input').change(function () {
    if ($(this).val() == 'physician-practices') {
      $('#physician-practice-conditional').slideDown();
    } else {
      $('#physician-practice-conditional').slideUp();
    }
  });

  // get the range value and display text label
  $(document).on('input change', '#wait-time-range-input', function () {
    if ($(this).val() == '1') {
      $('.location-range').html('15 Minute Limit');
    } else if ($(this).val() == '2') {
      $('.location-range').html('30 Minute Limit');
    } else if ($(this).val() == '3') {
      $('.location-range').html('1 Hour Limit');
    } else if ($(this).val() == '4') {
      $('.location-range').html('No Wait Time Limit');
    }
  });

  //  Trigger the mobile clinical filter dropdown
  $('#mobileClinicalFilterDropdown').click(function () {
    if ($(this).hasClass('js-is-open')) {
      // Close dropdown if click again
      $(this).removeClass('js-is-open');
      $(this).siblings('.rail-filters__dropdown').slideUp();
      $('.clinical-bar__filtertext-open').hide();
      $('.clinical-bar__filtertext-close').show();
    } else {
      // Open dropdown on click
      $(this).addClass('js-is-open');
      $(this).siblings('.rail-filters__dropdown').slideDown({
        inherit_select_classes: true,
      });
      $('.clinical-bar__filtertext-close').hide();
      $('.clinical-bar__filtertext-open').show();
    }
  });

  //  Trigger the tooltip on locations hours. Pulled from live prior to refresh
  $(document).on("click", ".location-list__hours", function (e) {
    e.preventDefault();
    if (!$(this).hasClass("open")) {
      $(".location-list__hours").removeClass("open");
      $(this).addClass("open")
    } else {
      $(".location-list__hours").removeClass("open")
    }
  });
  $(document).on("mouseout", ".location-list__hours", function (e) {
    if (window.outerWidth >= 900) {
      $(".location-list__hours").removeClass("open")
    }
  })

  /// /////////////////////////////////////
  // Sticky Sidenav Fix
  /// /////////////////////////////////////
  var offset = 165;
  var anchors = Array.from(document.querySelectorAll('.anchor-main .anchor-content__heading'));
  var aside = document.querySelector('.anchor-aside__list-container');
  // if (aside != null) {
  //     aside.style.position = 'sticky';
  //     aside.style.top = offset + 'px';
  // }
  var page = document.querySelector('.page');
  page.style.overflow = 'visible';

  function slug(str) {
    str = str.replace(/^\s+|\s+$/g, ''); // trim
    str = str.toLowerCase();

    // remove accents, swap ñ for n, etc
    var from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;";
    var to = "aaaaeeeeiiiioooouuuunc------";
    for (var i = 0, l = from.length; i < l; i++) {
      str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }

    str = str.replace(/[^a-z0-9 -]/g, '') // remove invalid chars
      .replace(/\s+/g, '-') // collapse whitespace and replace by -
      .replace(/-+/g, '-'); // collapse dashes

    return str;
  }

  function createAnchors() {

    anchors.forEach(function (a) {
      var ID = slug(a.innerText);
      var newAnchor = document.createElement('div');
      newAnchor.innerHTML = '&nbsp;';
      newAnchor.classList.add('atag');
      newAnchor.id = ID;
      newAnchor.style.height = 0;
      newAnchor.style.fontSize = 0;
      newAnchor.style.position = 'relative';
      newAnchor.style.top = -offset + 'px';
      a.parentNode.insertBefore(newAnchor, a);

    });
  }

  function createAside() {
    if (!aside) return;
    // Don't build the scroll-nav <ol> when there are no anchors to populate it
    if (!anchors || anchors.length === 0) return;
    var wrap = document.createElement('nav');
    wrap.setAttribute('aria-label', 'On this page');
    wrap.classList.add('scroll-nav');
    var nav = document.createElement('ol');
    nav.classList.add('aside-nav');
    nav.classList.add('scroll-nav__list');

    anchors.forEach(function (anchor) {
      var ID = slug(anchor.innerText);
      var li = document.createElement('li');
      li.classList.add('scroll-nav__item');
      var a = document.createElement('a');
      a.innerText = anchor.innerText;
      a.setAttribute('href', '#' + ID);
      a.classList.add('scroll-nav__link');
      li.appendChild(a);
      nav.appendChild(li);
    });
    wrap.appendChild(nav);
    aside.appendChild(wrap);

    if (window.location.hash != '') {
      $('html, body').animate({
        scrollTop: $(window.location.hash).offset().top
      }, 500, function () {
        window.location.hash = window.location.hash;
      });
    }
  }

  function setScrollSpy() {
    if (!aside) return;
    var items = Array.from(aside.querySelectorAll('.aside-nav a'));
    var tags = document.querySelectorAll('.anchor-main div.atag');

    window.addEventListener('scroll', function () {
      var offset = 100;

      tags.forEach(function (tag, i) {
        var top = tag.getBoundingClientRect().top;
        var id = tag.id;

        if (top <= offset) {
          var current = document.querySelector('.aside-nav li.scroll-nav__item--active');
          if (current) {
            current.classList.remove('scroll-nav__item--active');
          }
          var activeTag = document.querySelector('.aside-nav li a[href*=' + id + ']');
          activeTag.parentNode.classList.add('scroll-nav__item--active');
        }
      });
    });
  }

  function destroyOldScrollNav() {
    var page = document.querySelector('.page');
    page.style.overflow = 'visible';

    if (scrollnav) {
      try {
        scrollnav.destroy();
      } catch (e) { }
    }
  }

  function addCss() {
    var css = '.page{overflow:visible;}.anchor-aside {position:sticky !important;top:20px !important;max-width:initial !important;}html{scroll-behavior:smooth;}';
    var head = document.getElementsByTagName('head')[0];
    var s = document.createElement('style');
    s.setAttribute('type', 'text/css');
    if (s.styleSheet) {   // IE
      s.styleSheet.cssText = css;
    } else {                // the world
      s.appendChild(document.createTextNode(css));
    }
    head.appendChild(s);
  }

  function stickyIE() {
    if (window.navigator.userAgent.match(/MSIE|Trident/)) {
      const nav = document.querySelector('anchor-aside__list-container');
      const navY = nav.offsetTop;

      window.addEventListener('scroll', function (e) {
        const py = window.pageYOffset;
        if (py >= navY) {
          nav.style.position = 'fixed';
        } else {
          nav.style.position = '';
        }
      });
    } else {
      addCss();
    }
  }

  function init() {
    destroyOldScrollNav();
    createAnchors();
    createAside();
    setScrollSpy();
    stickyIE();

    if ($('.menus__hamburger-input').is(':checked')) {
      $('.menus__hamburger-input').prop('checked', false);
    }
  }
  setTimeout(function () {
      init();
  }, 300);


  var visibleReviews = 5;
  var reviewsContainer = $('#reviewsContainer');
  var reviews = $('.ratings-reviews__reviews-item');

  if (reviewsContainer.length) {
    for (var i = 0; i < visibleReviews; i++) {
      if (reviews.eq(i)) {
        reviews.eq(i).addClass('is-visible');
      }
    }

    if (visibleReviews >= reviews.length) {
      $('#seeMoreButton').hide();
    }

    $('#seeMoreButton').on('click', function () {
      var reviewsToDisplay = visibleReviews + 10 <= reviews.length ? 10 : reviews.length - visibleReviews;

      for (var i = visibleReviews; i < visibleReviews + reviewsToDisplay; i++) {
        if (reviews.eq(i)) {
          reviews.eq(i).addClass('is-visible');
        }
      }

      visibleReviews += reviewsToDisplay;

      if (visibleReviews >= reviews.length) {
        $('#seeMoreButton').hide();
      }
    });
  }

  /// /////////////////////////////////////
  // FAD Details Appointment Modal
  /// /////////////////////////////////////

  // Function to handle the Fad Detail Modal
  function handleFadDetailModal() {
    var fadDetailModal = $('.info-card__appt');
    var fadDetailModalClose = fadDetailModal.find('.info-card__appt-card-close');

    fadDetailModal.css('display', 'flex').addClass('is-open');

    function hideModal() {
      fadDetailModal.toggleClass('is-open', false).hide();
    }

    fadDetailModalClose.on('click', hideModal);

    fadDetailModal.on('click', function (event) {
      if ($(this).hasClass('is-open') && !$(event.target).hasClass('info-card__appt-btn')) {
        hideModal();
      }
    });
  }

  //click 'call for appointment' button opens modal on desktop
  $('.js-appt-btn').on('click', function (e) {
    if ($(window).width() >= 768) {
      e.preventDefault();
      handleFadDetailModal();
    }
  });

}(jQuery));
